import * as React from 'react';

import {
    ContentKitLifecycle,
    type ContentKitLifecycleRef,
    type ContentKitRemoteInput,
    useContentKitBlockControls,
} from '@gitbook/contentkit-react';
import type { BlockIntegrationData } from '@gitbook/doc-core';
import { webConfig } from '@gitbook/env';
import type { PaletteGroupDef } from '@gitbook/react-uikit';

import { contentKitRemoteComponents } from '../../ContentKit';
import { NoSSR } from '../../SSR';
import { BlockLoadingFallback } from '../common';
import { ContentEditorContext } from '../contexts';

export interface IntegrationBlockViewRef {
    getPaletteGroups: () => PaletteGroupDef[];
}

/**
 * Component to render the inner content of an integration block.
 */
export const IntegrationBlockView = React.forwardRef(function IntegrationBlockViewWithRef(
    props: {
        data: BlockIntegrationData;
        readOnly: boolean;
        onDataChange: (data: BlockIntegrationData) => void;
    },
    ref: React.Ref<IntegrationBlockViewRef>
): React.ReactElement {
    const { data, readOnly, onDataChange } = props;

    const { integrationBlocksRemote, integrationBlocksContext } =
        React.useContext(ContentEditorContext);
    const lifecycleRef = React.useRef<ContentKitLifecycleRef>(null);

    if (!integrationBlocksContext) {
        throw new Error('Expected integration blocks context to be defined');
    }

    const input: ContentKitRemoteInput = {
        integrationId: data.integration,
        componentId: data.block,
        props: data.props,
        action: data.action,
        context: integrationBlocksContext,
    };

    const [computeIntegrationPalette, integrationPaletteModal] = useContentKitBlockControls(
        async (action) => lifecycleRef.current?.dispatchAction(action)
    );

    // Compute the palette to render for the block
    // It uses the cache generated by the rendering of ContentKitLifecycle
    React.useImperativeHandle(ref, () => ({
        getPaletteGroups: () => {
            const { result } = integrationBlocksRemote.readCacheState(input);

            if (
                result &&
                (!result.type || result.type === 'element') &&
                result.element.type === 'block'
            ) {
                return computeIntegrationPalette(result.element);
            }

            return [];
        },
    }));

    return (
        <>
            {integrationPaletteModal}
            <React.Suspense fallback={<BlockLoadingFallback />}>
                <NoSSR>
                    <ContentKitLifecycle
                        ref={lifecycleRef}
                        mode="block"
                        remote={integrationBlocksRemote}
                        remoteComponents={contentKitRemoteComponents}
                        input={input}
                        security={{
                            firstPartyDomains: [webConfig.integrations.domain],
                        }}
                        onPropsChange={async (newProps) => {
                            if (!readOnly) {
                                await integrationBlocksRemote.readAsync({
                                    ...input,
                                    props: newProps,
                                });

                                onDataChange({
                                    ...data,
                                    action: undefined,
                                    props: newProps,
                                });
                            }
                        }}
                    />
                </NoSSR>
            </React.Suspense>
        </>
    );
});
